import { AfterViewInit, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { IUser } from 'projects/common/src/app/interfaces';
import { IMachineContext } from 'projects/common/src/app/interfaces/machine-context';
import { IMachineType } from 'projects/common/src/app/interfaces/machine-type';
import { ParameterHandlerService } from 'projects/common/src/app/services';
import { LanguageService } from 'projects/common/src/app/services/parameters';
import { takeWhile } from 'rxjs/operators';
import { IShift } from 'src/app/modules/core/interfaces/shift';
import { BooleanStateService } from 'src/app/modules/core/services/boolean-state.service';
import { ShiftCollectionService, ShiftService } from 'src/app/modules/core/services/parameter';
import { WidgetRequestComponent } from 'src/app/modules/widget/components/widget-request/widget-request.component';
import { ScreenViewService } from 'src/app/modules/widget/services/screen-view.service';
import { CommunicationService } from 'src/app/services/communication';
import { CacheService } from '../../../../../../../../projects/common/src/app/services/cache.service';
import { IStoredProcedureInputs } from '../../../../interfaces/chart-widget';

@Component({
  selector: 'app-shift-group',
  templateUrl: './shift-group.component.html',
  styleUrls: ['./shift-group.component.scss'],
  providers: [BooleanStateService],
})
export class ShiftGroupComponent extends WidgetRequestComponent implements OnInit, AfterViewInit {
  public visibilitySizeStatus = false;
  public shiftClass = '';
  public viewCollection: IShift[] = [];
  public hasProduction = false;
  public isInitialLoad = true;
  public numerator = '';
  public denominator = '';
  private totalViewModel: IShift;

  constructor(
    public progressBarService: BooleanStateService,
    public communication: CommunicationService,
    public parameterService: ParameterHandlerService,
    private shiftService: ShiftService,
    private languageService: LanguageService,
    private shiftCollectionService: ShiftCollectionService,
    private cacheService: CacheService,
    protected screenViewService: ScreenViewService,
    private cdr: ChangeDetectorRef,
  ) {
    super(progressBarService, communication, screenViewService);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.initializeShifts();
  }

  ngAfterViewInit(): void {
    this.subscribeToMachineContext();
    setTimeout(() => {
      this.isInitialLoad = false;
      this.cdr.detectChanges();
    }, 0);
  }

  private initializeShifts(): void {
    const shifts = this.getShiftsFromParameters();
    if (!shifts.length) {
      console.warn('No shifts found in shift-collection. Waiting for updates...');
      this.parameterService.parameterSource
        .item('shift-collection')
        .pipe(takeWhile(() => !this.componentDestroyed))
        .subscribe((updatedShifts) => {
          if (updatedShifts?.length) {
            this.initialize(updatedShifts); // Re-inicializa la vista con los nuevos datos
          }
        });
    } else {
      this.initialize(shifts);
    }
  }

  private getShiftsFromParameters(): IShift[] {
    const shifts = this.parameterService.parameters.item('shift-collection')?.value || [];
    return shifts;
  }

  private subscribeToMachineContext(): void {
    const machine = this.parameterService.parameters.item('machine-context').value;
    this.shiftCollectionService.start(machine.machineTypeId);
    const shifts = this.getShiftsFromParameters();
    this.initialize(shifts);
    this.cdr.detectChanges();
  }

  public buildRequestParams(): IStoredProcedureInputs {
    const machineCollection = (this.parameterService.parameters.item('machine-collection')?.value as IMachineType[]) || [];
    const currentMachine = this.parameterService.parameters.item('machine-context')?.value?.name || '';
    const companyId = (this.parameterService.parameters.item('user-data')?.value as IUser)?.company?.id || 0;
    const dateValue = this.parameterService.parameters.item('date')?.value || '';
    const zoneId = this.cacheService.session('selectedZone')?.zoneId || 0;

    const machineType = machineCollection.find((value) => value.name?.toLowerCase() === currentMachine.toLowerCase())?.machineTypeId;

    return { machineTypeId: machineType, companyId, inputDate: dateValue, zoneId };
  }

  public buildView(data: IShift[]): void {
    if (!data?.length) return;

    this.numerator = data[0].numerator || '';
    this.denominator = data[0].denominator || '';

    // Obtener el contexto de la máquina actual desde el paramService
    const machineContext = this.parameterService.parameters.item('machine-context')?.value as IMachineContext;

    if (machineContext?.hasProduction !== null && machineContext?.hasProduction !== undefined) {
      this.hasProduction = machineContext.hasProduction;
    } else {
      // Obtener la colección de máquinas desde el sessionStorage si falla el hasProduction
      const machineCollection = (this.parameterService.parameters.item('machine-collection')?.value as IMachineType[]) || [];
      const machineName = machineContext?.name || '';

      const machineData = machineCollection.find((machine) => machine.name?.toLowerCase() === machineName.toLowerCase());

      // Si la máquina existe en la colección, asignar su hasProduction
      this.hasProduction = machineData?.hasProduction ?? false;
    }

    this.updateViewCollection(data);
  }

  private updateViewCollection(data: IShift[]): void {
    const mainShift = this.viewCollection[0];
    mainShift.totalMeasure = 0;
    mainShift.countMeasure = 0;

    let gradeSum = 0;
    let gradeCount = 0;

    for (const value of data) {
      const shift = this.viewCollection.find((x) => x.shiftId === value.shiftId);
      if (shift) {
        shift.totalMeasure = parseFloat(value.totalMeasure.toFixed(2)) || 0;
        shift.countMeasure = parseFloat(value.countMeasure.toFixed(2)) || 0;
        shift.grade = value.grade;

        mainShift.totalMeasure += shift.totalMeasure;
        mainShift.countMeasure += shift.countMeasure;

        if (value.grade !== null) {
          gradeSum += value.grade;
          gradeCount += 1;
        }
      }
    }

    mainShift.grade = gradeCount > 0 ? parseFloat((gradeSum / gradeCount).toFixed(2)) : null;
    mainShift.totalMeasure = parseFloat(mainShift.totalMeasure.toFixed(2));
  }

  private initialize(shifts: IShift[]): void {
    this.totalViewModel = this.createTotalViewModel(shifts);
    this.viewCollection = [this.totalViewModel];

    if (shifts?.length) {
      this.addShiftsToViewCollection(shifts);
      this.selectCurrentShift();
    }
  }

  private createTotalViewModel(shifts: IShift[]): IShift {
    return {
      shiftId: -1,
      name: this.languageService.message('day_detail'),
      numerator: shifts?.[0]?.numerator || 'tons',
      denominator: shifts?.[0]?.denominator || 'tons',
      totalMeasure: 0,
      countMeasure: 0,
      active: false,
    } as IShift;
  }

  private addShiftsToViewCollection(shifts: IShift[]): void {
    shifts
      .sort((a, b) => a.shiftOrder - b.shiftOrder)
      .forEach((shift) => {
        this.viewCollection.push({ ...shift, totalMeasure: 0, countMeasure: 0, active: false });
      });
    this.defineClass(this.viewCollection.length);
  }

  private selectCurrentShift(): void {
    const currentShift = this.shiftService.currentShift();
    this.shiftSelected(currentShift || this.viewCollection.find((shift) => !!shift.shiftOrder));
  }

  public shiftSelected(selectedShift: IShift): void {
    this.viewCollection.forEach((shift) => {
      shift.active = shift.name === selectedShift?.name;
    });
    this.parameterService.setParameter('shift', selectedShift);
  }

  private defineClass(length: number): void {
    const col = Math.max(1, Math.floor(12 / length));
    this.shiftClass = `col-sm-${col} col-md-${col} col-lg-${col}`;
  }
}
